import React, { useState } from 'react';

// Visitor & Tourist Visa Step 4: AI Application Analysis & Risk Assessment
export const VisitorStep4ApplicationAnalysis = ({ formData, setFormData, onNext, onBack }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(formData.analysisResults || {});

  const performRealAnalysis = async () => {
    setAnalyzing(true);
    
    try {
      // Real API call to analyze visitor visa application
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const response = await fetch(`${backendUrl}/api/ai/analyze-case`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          case_type: `${formData.visaType?.toUpperCase()} Visitor Visa Application`,
          petitioner_details: {
            fullName: formData.personalDetails?.fullName || "Visitor Visa Applicant",
            status: "visitor-applicant",
            employment: "Various",
            income: "0",
            address: formData.personalDetails?.currentAddress || "Home Country"
          },
          beneficiary_details: {
            fullName: formData.personalDetails?.fullName || "Visitor Visa Applicant",
            status: "visitor-applicant", 
            country: formData.personalDetails?.citizenship || "Home Country",
            location: formData.personalDetails?.currentAddress || "Home Country"
          },
          relationship_details: {
            visaType: formData.visaType || "b2",
            purposeOfVisit: formData.purposeOfVisit || "",
            plannedDuration: formData.personalDetails?.duration || "",
            previousUSVisits: formData.travelHistory?.previousUSVisits || "none",
            visaRefusals: formData.travelHistory?.visaRefusals || "none",
            travelExperience: formData.travelHistory?.internationalTravel || "",
            plannedArrival: formData.personalDetails?.plannedArrival || "",
            plannedDeparture: formData.personalDetails?.plannedDeparture || ""
          },
          documents: (formData.documents || []).map(doc => {
            // Create a copy without the file object for JSON serialization
            const { file, ...docWithoutFile } = doc;
            return docWithoutFile;
          }),
        })
      });

      if (response.ok) {
        const result = await response.json();
        setAnalysisResults(result);
        setFormData({ ...formData, analysisResults: result });
      } else {
        // Fallback to dynamic analysis if API fails
        const dynamicAnalysis = generateVisitorAnalysis(formData);
        setAnalysisResults(dynamicAnalysis);
        setFormData({ ...formData, analysisResults: dynamicAnalysis });
      }
    } catch (error) {
      console.error('Analysis error:', error);
      // Fallback to dynamic analysis
      const dynamicAnalysis = generateVisitorAnalysis(formData);
      setAnalysisResults(dynamicAnalysis);
      setFormData({ ...formData, analysisResults: dynamicAnalysis });
    }
    
    setAnalyzing(false);
  };

  const generateVisitorAnalysis = (data) => {
    const documents = data.documents || [];
    const personalDetails = data.personalDetails || {};
    const travelHistory = data.travelHistory || {};
    const visaType = data.visaType || 'b2';
    
    // Calculate application strength using algorithms
    let applicationScore = 25; // Base score
    let documentScore = 0;
    let tiesScore = 0;
    let travelScore = 0;
    let riskFactors = [];

    // Document analysis scoring
    const requiredDocs = ['passport', 'financial', 'travel', 'ties'];
    const foundDocs = requiredDocs.filter(reqDoc => 
      documents.some(doc => doc.category?.includes(reqDoc) || doc.name.toLowerCase().includes(reqDoc))
    );
    documentScore = (foundDocs.length / requiredDocs.length) * 25;

    // Ties to home country scoring
    const homeCountryTies = documents.filter(doc => 
      doc.category === 'ties-to-home' || 
      doc.category === 'financial-support'
    ).length;
    tiesScore = Math.min(homeCountryTies * 5, 20);

    // Travel history scoring
    const travelExp = travelHistory.internationalTravel || 'limited';
    const prevUSVisits = travelHistory.previousUSVisits || 'none';
    const visaRefusals = travelHistory.visaRefusals || 'none';

    if (travelExp === 'extensive' || travelExp === 'business') travelScore += 15;
    else if (travelExp === 'moderate') travelScore += 10;
    else if (travelExp === 'limited') travelScore += 5;

    if (prevUSVisits === 'multiple' || prevUSVisits === 'frequent') travelScore += 10;
    else if (prevUSVisits === 'once') travelScore += 5;

    if (visaRefusals !== 'none') {
      travelScore -= 10;
      riskFactors.push('Previous visa refusals may impact application');
    }

    // Calculate duration appropriateness
    const duration = personalDetails.duration || '';
    if (duration === '1-2weeks' || duration === '3-4weeks') travelScore += 5;
    else if (duration === '3-6months') travelScore -= 5;

    // Financial support assessment
    const financialDocs = documents.filter(doc => 
      doc.category === 'financial-support'
    ).length;
    
    if (financialDocs === 0) {
      riskFactors.push('Limited financial documentation provided');
    }

    const finalScore = Math.min(100, Math.max(0, applicationScore + documentScore + tiesScore + travelScore));

    // Generate specific analysis
    const strengths = [];
    const risks = [];
    const recommendations = [];

    // Strengths
    if (travelExp === 'extensive') {
      strengths.push('Extensive international travel history demonstrates compliance');
    }
    if (prevUSVisits === 'multiple' || prevUSVisits === 'frequent') {
      strengths.push('Previous successful US visits strengthen credibility');
    }
    if (documents.length >= 8) {
      strengths.push('Comprehensive documentation package');
    }
    if (duration === '1-2weeks' || duration === '3-4weeks') {
      strengths.push('Appropriate trip duration for tourism/business');
    }
    if (homeCountryTies >= 3) {
      strengths.push('Strong ties to home country documented');
    }

    // Risk factors
    if (visaRefusals !== 'none') {
      risks.push('Previous visa refusals require careful explanation');
    }
    if (duration === '3-6months') {
      risks.push('Long stay duration may raise immigration intent concerns');
    }
    if (documents.length < 4) {
      risks.push('Limited documentation may weaken application');
    }
    if (travelExp === 'limited') {
      risks.push('Limited travel history may indicate higher risk');
    }
    if (financialDocs === 0) {
      risks.push('Insufficient financial support documentation');
    }

    // Recommendations
    if (financialDocs < 2) {
      recommendations.push('Provide additional financial support documentation');
    }
    if (homeCountryTies < 2) {
      recommendations.push('Include more evidence of ties to home country');
    }
    if (visaRefusals !== 'none') {
      recommendations.push('Prepare detailed explanation for previous visa refusals');
    }
    if (documents.length < 6) {
      recommendations.push('Upload additional supporting documents');
    }
    if (!personalDetails.plannedArrival) {
      recommendations.push('Provide specific travel dates and itinerary');
    }

    return {
      overall_score: finalScore,
      application_strength: finalScore >= 80 ? 'Strong' : finalScore >= 60 ? 'Good' : finalScore >= 40 ? 'Fair' : 'Weak',
      document_analysis: {
        total_documents: documents.length,
        required_found: foundDocs.length,
        completeness: foundDocs.length >= 3 ? 'Good' : foundDocs.length >= 2 ? 'Partial' : 'Insufficient',
        score: documentScore
      },
      risk_assessment: {
        travel_history_risk: travelExp === 'limited' ? 'Medium' : 'Low',
        visa_refusal_risk: visaRefusals !== 'none' ? 'High' : 'Low',
        duration_risk: duration === '3-6months' ? 'Medium' : 'Low',
        financial_risk: financialDocs === 0 ? 'High' : financialDocs === 1 ? 'Medium' : 'Low',
        overall_risk: riskFactors.length > 2 ? 'High' : riskFactors.length > 0 ? 'Medium' : 'Low'
      },
      ties_analysis: {
        home_country_ties: homeCountryTies >= 3 ? 'Strong' : homeCountryTies >= 1 ? 'Moderate' : 'Weak',
        employment_ties: documents.some(d => d.category === 'ties-to-home') ? 'Documented' : 'Not documented',
        family_ties: personalDetails.currentAddress ? 'Documented' : 'Not documented',
        score: tiesScore
      },
      strengths: strengths.length > 0 ? strengths : ['Application information collected'],
      risks: risks.length > 0 ? risks : ['No major risks identified'],
      recommendations: recommendations.length > 0 ? recommendations : ['Continue with standard application process']
    };
  };

  React.useEffect(() => {
    if (Object.keys(analysisResults).length === 0) {
      performRealAnalysis();
    }
  }, []);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">AI Application Analysis & Risk Assessment</h3>
        <p className="text-gray-400 text-lg">Comprehensive analysis of your visa application strength</p>
      </div>

      {analyzing && (
        <div className="bg-gray-800 p-8 rounded-lg text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <h4 className="text-xl text-white mb-2">Analyzing Your Visa Application</h4>
          <p className="text-gray-400">Evaluating documents, travel history, and application strength...</p>
        </div>
      )}

      {!analyzing && Object.keys(analysisResults).length > 0 && (
        <>
          {/* Overall Assessment */}
          <div className="bg-gradient-to-r from-blue-900/20 to-gray-900/20 p-8 rounded-lg border border-blue-400/30">
            <div className="text-center">
              <div className="text-6xl font-bold text-blue-400 mb-2">{analysisResults.overall_score}%</div>
              <div className="text-xl text-white mb-4">Visa Application Strength</div>
              <div className={`text-lg ${
                analysisResults.overall_score >= 80 ? 'text-emerald-400' :
                analysisResults.overall_score >= 60 ? 'text-yellow-400' :
                'text-red-400'
              }`}>
                {analysisResults.application_strength}
              </div>
            </div>
          </div>

          {/* Detailed Analysis */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-800 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-white mb-4">📋 Document Analysis</h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-300">Total Documents:</span>
                  <span className="text-blue-400 font-medium">{analysisResults.document_analysis?.total_documents}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Required Found:</span>
                  <span className="text-blue-400 font-medium">{analysisResults.document_analysis?.required_found}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Completeness:</span>
                  <span className={`font-medium ${
                    analysisResults.document_analysis?.completeness === 'Good' ? 'text-emerald-400' :
                    analysisResults.document_analysis?.completeness === 'Partial' ? 'text-yellow-400' :
                    'text-red-400'
                  }`}>
                    {analysisResults.document_analysis?.completeness}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Doc Score:</span>
                  <span className="text-blue-400 font-medium">{Math.round(analysisResults.document_analysis?.score || 0)}/25</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-white mb-4">🏠 Ties to Home Country</h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-300">Home Country Ties:</span>
                  <span className={`font-medium ${
                    analysisResults.ties_analysis?.home_country_ties === 'Strong' ? 'text-emerald-400' :
                    analysisResults.ties_analysis?.home_country_ties === 'Moderate' ? 'text-yellow-400' :
                    'text-red-400'
                  }`}>
                    {analysisResults.ties_analysis?.home_country_ties}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Employment Ties:</span>
                  <span className={`font-medium ${
                    analysisResults.ties_analysis?.employment_ties === 'Documented' ? 'text-emerald-400' : 'text-red-400'
                  }`}>
                    {analysisResults.ties_analysis?.employment_ties}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Family Ties:</span>
                  <span className={`font-medium ${
                    analysisResults.ties_analysis?.family_ties === 'Documented' ? 'text-emerald-400' : 'text-red-400'
                  }`}>
                    {analysisResults.ties_analysis?.family_ties}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Ties Score:</span>
                  <span className="text-blue-400 font-medium">{Math.round(analysisResults.ties_analysis?.score || 0)}/20</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-white mb-4">⚠️ Risk Assessment</h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-300">Travel History:</span>
                  <span className={`font-medium ${
                    analysisResults.risk_assessment?.travel_history_risk === 'Low' ? 'text-emerald-400' :
                    analysisResults.risk_assessment?.travel_history_risk === 'Medium' ? 'text-yellow-400' :
                    'text-red-400'
                  }`}>
                    {analysisResults.risk_assessment?.travel_history_risk}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Visa Refusal Risk:</span>
                  <span className={`font-medium ${
                    analysisResults.risk_assessment?.visa_refusal_risk === 'Low' ? 'text-emerald-400' :
                    analysisResults.risk_assessment?.visa_refusal_risk === 'Medium' ? 'text-yellow-400' :
                    'text-red-400'
                  }`}>
                    {analysisResults.risk_assessment?.visa_refusal_risk}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Financial Risk:</span>
                  <span className={`font-medium ${
                    analysisResults.risk_assessment?.financial_risk === 'Low' ? 'text-emerald-400' :
                    analysisResults.risk_assessment?.financial_risk === 'Medium' ? 'text-yellow-400' :
                    'text-red-400'
                  }`}>
                    {analysisResults.risk_assessment?.financial_risk}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Overall Risk:</span>
                  <span className={`font-medium ${
                    analysisResults.risk_assessment?.overall_risk === 'Low' ? 'text-emerald-400' :
                    analysisResults.risk_assessment?.overall_risk === 'Medium' ? 'text-yellow-400' :
                    'text-red-400'
                  }`}>
                    {analysisResults.risk_assessment?.overall_risk}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Strengths and Risks */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-emerald-900/20 border border-emerald-400 p-6 rounded-lg">
              <h4 className="text-emerald-300 font-semibold mb-4">✅ Application Strengths</h4>
              <ul className="space-y-2">
                {analysisResults.strengths?.map((strength, index) => (
                  <li key={index} className="text-emerald-200 text-sm flex items-start">
                    <span className="text-emerald-400 mr-2 mt-1">•</span>
                    {strength}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-red-900/20 border border-red-400 p-6 rounded-lg">
              <h4 className="text-red-300 font-semibold mb-4">⚠️ Potential Risks</h4>
              <ul className="space-y-2">
                {analysisResults.risks?.map((risk, index) => (
                  <li key={index} className="text-red-200 text-sm flex items-start">
                    <span className="text-red-400 mr-2 mt-1">•</span>
                    {risk}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Recommendations */}
          <div className="bg-yellow-900/20 border border-yellow-400 p-6 rounded-lg">
            <h4 className="text-yellow-300 font-semibold mb-4">💡 Recommendations</h4>
            <ul className="space-y-2">
              {analysisResults.recommendations?.map((rec, index) => (
                <li key={index} className="text-yellow-200 text-sm flex items-start">
                  <span className="text-yellow-400 mr-2 mt-1">{index + 1}.</span>
                  {typeof rec === 'string' ? rec : (
                    <div>
                      <div className="font-medium text-yellow-300 mb-1">{rec.category}</div>
                      {rec.items?.map((item, itemIndex) => (
                        <div key={itemIndex} className="ml-2 mb-1">• {item}</div>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Document Upload
        </button>
        <button
          onClick={onNext}
          disabled={analyzing}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            !analyzing
              ? 'bg-blue-500 text-white hover:bg-blue-600'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Interview Preparation →
        </button>
      </div>
    </div>
  );
};

// Visitor & Tourist Visa Step 5: Interview Preparation & Tips
export const VisitorStep5InterviewPreparation = ({ formData, setFormData, onNext, onBack }) => {
  const [preparationProgress, setPreparationProgress] = useState(formData.preparationProgress || {});

  const interviewCategories = [
    {
      id: 'purpose-questions',
      title: 'Purpose of Visit Questions',
      icon: '🎯',
      questions: [
        'What is the purpose of your visit to the United States?',
        'How long do you plan to stay?',
        'Where will you be staying?',
        'Who will you be visiting/meeting?',
        'What will you do during your visit?'
      ],
      tips: [
        'Be specific and consistent with your application',
        'Prepare a clear, concise explanation',
        'Bring supporting documents (itinerary, invitations)',
        'Show genuine purpose for your trip'
      ]
    },
    {
      id: 'ties-questions',
      title: 'Ties to Home Country',
      icon: '🏠',
      questions: [
        'What do you do for work in your home country?',
        'Do you own property or have family ties at home?',
        'What will bring you back to your home country?',
        'How long have you lived at your current address?',
        'Do you have dependents in your home country?'
      ],
      tips: [
        'Emphasize strong ties that ensure your return',
        'Bring employment letters and property documents',
        'Discuss family responsibilities and commitments',
        'Show you have a life to return to'
      ]
    },
    {
      id: 'financial-questions',
      title: 'Financial Support Questions',
      icon: '💰',
      questions: [
        'How will you pay for your trip?',
        'What is your annual income?',
        'Who is sponsoring your trip?',
        'Do you have enough money for your stay?',
        'What are your monthly expenses?'
      ],
      tips: [
        'Bring bank statements and pay stubs',
        'Show you can afford the trip without working',
        'If sponsored, bring sponsor documentation',
        'Be honest about your financial situation'
      ]
    },
    {
      id: 'travel-questions',
      title: 'Travel History Questions',
      icon: '✈️',
      questions: [
        'Have you traveled to other countries before?',
        'Have you been to the US before?',
        'Have you ever been refused a visa?',
        'Have you ever overstayed in any country?',
        'Why didn\'t you visit the US earlier?'
      ],
      tips: [
        'Be honest about all travel history',
        'Bring previous passports if available',
        'Explain any visa refusals honestly',
        'Show compliance with previous travel'
      ]
    }
  ];

  const redFlags = [
    {
      category: 'Immigration Intent',
      flags: [
        'Vague or inconsistent purpose of visit',
        'No specific return date or indefinite stay',
        'Weak ties to home country',
        'Previous overstays or violations'
      ],
      prevention: [
        'Be specific about your travel plans',
        'Show strong home country connections',
        'Demonstrate intent to return',
        'Address any past issues honestly'
      ]
    },
    {
      category: 'Financial Concerns',
      flags: [
        'Insufficient funds for the trip',
        'Unclear funding sources',
        'Unemployment without clear support',
        'Inconsistent financial information'
      ],
      prevention: [
        'Provide clear financial documentation',
        'Show legitimate funding sources',
        'Explain employment status honestly',
        'Bring bank statements and income proof'
      ]
    },
    {
      category: 'Documentation Issues',
      flags: [
        'Missing or incomplete documents',
        'Fraudulent or suspicious documents',
        'Inconsistencies between documents',
        'Poor quality or unclear copies'
      ],
      prevention: [
        'Prepare all required documents',
        'Ensure all documents are genuine',
        'Check for consistency across all papers',
        'Bring clear, legible copies'
      ]
    }
  ];

  const handleProgressUpdate = (categoryId, completed) => {
    const updated = { ...preparationProgress, [categoryId]: completed };
    setPreparationProgress(updated);
    setFormData({ ...formData, preparationProgress: updated });
  };

  const getPreparationScore = () => {
    const totalCategories = interviewCategories.length;
    const completedCategories = Object.values(preparationProgress).filter(Boolean).length;
    return totalCategories > 0 ? (completedCategories / totalCategories) * 100 : 0;
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Interview Preparation & Tips</h3>
        <p className="text-gray-400 text-lg">Prepare for your visa interview with comprehensive guidance</p>
      </div>

      {/* Preparation Progress */}
      <div className="bg-gradient-to-r from-blue-900/20 to-gray-900/20 p-8 rounded-lg border border-blue-400/30">
        <div className="text-center">
          <div className="text-6xl font-bold text-blue-400 mb-2">{Math.round(getPreparationScore())}%</div>
          <div className="text-xl text-white mb-4">Interview Preparation Complete</div>
          <div className={`text-lg ${
            getPreparationScore() >= 80 ? 'text-emerald-400' :
            getPreparationScore() >= 50 ? 'text-yellow-400' :
            'text-red-400'
          }`}>
            {getPreparationScore() >= 80 ? 'Well Prepared - Ready for Interview' :
             getPreparationScore() >= 50 ? 'Good Progress - Continue Preparation' :
             'More Preparation Needed'}
          </div>
        </div>
      </div>

      {/* Interview Question Categories */}
      <div className="space-y-6">
        <h4 className="text-2xl font-semibold text-white">📝 Common Interview Questions by Category</h4>
        
        {interviewCategories.map((category) => (
          <div key={category.id} className="bg-gray-800 p-6 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <span className="text-3xl mr-4">{category.icon}</span>
                <h5 className="text-xl font-semibold text-white">{category.title}</h5>
              </div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={preparationProgress[category.id] || false}
                  onChange={(e) => handleProgressUpdate(category.id, e.target.checked)}
                  className="w-5 h-5 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-300">Prepared</span>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h6 className="text-blue-300 font-medium mb-3">Common Questions:</h6>
                <ul className="space-y-2">
                  {category.questions.map((question, index) => (
                    <li key={index} className="text-gray-300 text-sm flex items-start">
                      <span className="text-blue-400 mr-2 mt-1">Q:</span>
                      {question}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h6 className="text-emerald-300 font-medium mb-3">Preparation Tips:</h6>
                <ul className="space-y-2">
                  {category.tips.map((tip, index) => (
                    <li key={index} className="text-emerald-200 text-sm flex items-start">
                      <span className="text-emerald-400 mr-2 mt-1">•</span>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Red Flag Alerts */}
      <div className="bg-red-900/20 border border-red-400 p-6 rounded-lg">
        <h4 className="text-red-300 font-semibold mb-4">🚨 Red Flag Alerts & Prevention</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {redFlags.map((redFlag, index) => (
            <div key={index} className="space-y-4">
              <h5 className="text-red-300 font-medium">{redFlag.category}</h5>
              
              <div>
                <h6 className="text-red-400 text-sm font-medium mb-2">Watch Out For:</h6>
                <ul className="space-y-1">
                  {redFlag.flags.map((flag, idx) => (
                    <li key={idx} className="text-red-200 text-xs flex items-start">
                      <span className="text-red-400 mr-1">⚠</span>
                      {flag}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h6 className="text-green-400 text-sm font-medium mb-2">Prevention:</h6>
                <ul className="space-y-1">
                  {redFlag.prevention.map((prevent, idx) => (
                    <li key={idx} className="text-green-200 text-xs flex items-start">
                      <span className="text-green-400 mr-1">✓</span>
                      {prevent}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* General Interview Tips */}
      <div className="bg-blue-900/20 border border-blue-400 p-6 rounded-lg">
        <h4 className="text-blue-300 font-semibold mb-4">💡 General Interview Success Tips</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h5 className="text-blue-300 font-medium mb-3">Before the Interview:</h5>
            <ul className="text-blue-200 text-sm space-y-1">
              <li>• Arrive early and dress professionally</li>
              <li>• Organize all documents in a clear folder</li>
              <li>• Review your application thoroughly</li>
              <li>• Practice answers to common questions</li>
              <li>• Get a good night's sleep</li>
            </ul>
          </div>
          
          <div>
            <h5 className="text-blue-300 font-medium mb-3">During the Interview:</h5>
            <ul className="text-blue-200 text-sm space-y-1">
              <li>• Be honest and consistent</li>
              <li>• Answer only what is asked</li>
              <li>• Speak clearly and confidently</li>
              <li>• Provide documents when requested</li>
              <li>• Stay calm and polite</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Application Analysis
        </button>
        <button
          onClick={onNext}
          className="px-6 py-3 rounded-lg font-medium transition-colors bg-blue-500 text-white hover:bg-blue-600"
        >
          Continue to Application Package →
        </button>
      </div>
    </div>
  );
};

// Visitor & Tourist Visa Step 6: Generate Application Package
export const VisitorStep6GeneratePackage = ({ formData, setFormData, onNext, onBack }) => {
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [package_, setPackage] = useState(formData.package || null);

  const generateApplicationPackage = async () => {
    setGenerating(true);
    
    try {
      // Real API call to generate visitor visa application package
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      
      const response = await fetch(`${backendUrl}/api/ai/generate-package`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          case_type: `${formData.visaType?.toUpperCase()} Visitor Visa Application Package`,
          petitioner_details: {
            fullName: formData.personalDetails?.fullName || "Visitor Visa Applicant",
            status: "visitor-applicant",
            employment: "Various",
            income: "0",
            address: formData.personalDetails?.currentAddress || "Home Country"
          },
          beneficiary_details: {
            fullName: formData.personalDetails?.fullName || "Visitor Visa Applicant",
            status: "visitor-applicant", 
            country: formData.personalDetails?.citizenship || "Home Country",
            location: formData.personalDetails?.currentAddress || "Home Country"
          },
          relationship_details: {
            visaType: formData.visaType || "b2",
            purposeOfVisit: formData.purposeOfVisit || "",
            plannedDuration: formData.personalDetails?.duration || "",
            previousUSVisits: formData.travelHistory?.previousUSVisits || "none",
            plannedArrival: formData.personalDetails?.plannedArrival || "",
            plannedDeparture: formData.personalDetails?.plannedDeparture || ""
          },
          documents: (formData.documents || []).map(doc => {
            // Create a copy without the file object for JSON serialization
            const { file, ...docWithoutFile } = doc;
            return docWithoutFile;
          }),
          analysis_results: formData.analysisResults || {},
          final_score: formData.analysisResults?.overall_score || 50
        })
      });

      if (response.ok) {
        const result = await response.json();
        const enhancedResult = {
          ...result,
          contents: result.package_contents || [
            'Visitor_Visa_Application_Guide.docx',
            'Interview_Preparation_Checklist.docx',
            'Purpose_of_Visit_Statement.docx',
            'Financial_Support_Summary.docx',
            'Travel_Itinerary_Template.docx',
            'User_Uploaded_Documents/',
            'README.txt'
          ],
          id: result.package_id || `VISITOR-${Date.now()}`,
          downloadUrl: result.download_url || `${backendUrl}/api/download-package/${result.package_id}`,
          score: formData.analysisResults?.overall_score || 0,
          title: `${formData.visaType?.toUpperCase() || 'B2'} Visitor Visa Package`,
          created: new Date().toISOString(),
          expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
        };
        setPackage(enhancedResult);
        setFormData({ ...formData, package: enhancedResult });
      } else {
        // Fallback to simulated package generation
        const simulatedPackage = generateVisitorPackage(formData);
        setPackage(simulatedPackage);
        setFormData({ ...formData, package: simulatedPackage });
      }
    } catch (error) {
      console.error('Package generation error:', error);
      // Fallback to simulated package generation
      const simulatedPackage = generateVisitorPackage(formData);
      setPackage(simulatedPackage);
      setFormData({ ...formData, package: simulatedPackage });
    }
    
    setGenerating(false);
  };

  const generateVisitorPackage = (data) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

    return {
      id: `VISITOR-${Date.now()}`,
      type: 'visitor-visa-application-packet',
      title: `${data.visaType?.toUpperCase() || 'B2'} Visitor Visa Package`,
      created: new Date().toISOString(),
      score: data.analysisResults?.overall_score || 0,
      contents: [
        'Visitor_Visa_Application_Guide.docx',
        'Interview_Preparation_Checklist.docx',
        'Purpose_of_Visit_Statement.docx',
        'Financial_Support_Summary.docx',
        'Travel_Itinerary_Template.docx',
        `User_Uploaded_Documents/ (${(data.documents || []).length} files)`,
        'DS-160_Application_Tips.docx',
        'README.txt'
      ],
      downloadUrl: `${backendUrl}/api/download-package/VISITOR-${Date.now()}`,
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
    };
  };

  const downloadPackage = async () => {
    if (package_ && !downloading) {
      setDownloading(true);
      
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        
        // Always try to generate fresh package
        const generateResponse = await fetch(`${backendUrl}/api/ai/generate-package`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            case_type: `${formData.visaType?.toUpperCase()} Visitor Visa Application Package`,
            petitioner_details: {
              fullName: formData.personalDetails?.fullName || "Visitor Visa Applicant",
              status: "visitor-applicant",
              employment: "Various",
              income: "0",
              address: formData.personalDetails?.currentAddress || "Home Country"
            },
            beneficiary_details: {
              fullName: formData.personalDetails?.fullName || "Visitor Visa Applicant",
              status: "visitor-applicant", 
              country: formData.personalDetails?.citizenship || "Home Country",
              location: formData.personalDetails?.currentAddress || "Home Country"
            },
            relationship_details: {
              visaType: formData.visaType || "b2",
              purposeOfVisit: formData.purposeOfVisit || "",
              plannedDuration: formData.personalDetails?.duration || ""
            },
            documents: (formData.documents || []).map(doc => {
              // Create a copy without the file object for JSON serialization
              const { file, ...docWithoutFile } = doc;
              return docWithoutFile;
            }),
            analysis_results: formData.analysisResults || {},
            final_score: formData.analysisResults?.overall_score || 50
          })
        });

        if (generateResponse.ok) {
          const generateResult = await generateResponse.json();
          const downloadUrl = generateResult.download_url || `${backendUrl}/api/download-package/${generateResult.package_id}`;
          
          const downloadResponse = await fetch(downloadUrl);
          if (downloadResponse.ok) {
            const blob = await downloadResponse.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `visitor-visa-application-packet-${generateResult.package_id}.zip`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            // Update the package data
            const updatedPackage = {
              ...package_,
              id: generateResult.package_id,
              downloadUrl: downloadUrl
            };
            setPackage(updatedPackage);
            setFormData({ ...formData, package: updatedPackage });
          } else {
            throw new Error(`Download failed: ${downloadResponse.status}`);
          }
        } else {
          throw new Error(`Package generation failed: ${generateResponse.status}`);
        }
        
      } catch (error) {
        console.error('Download error:', error);
        alert(`Download failed: ${error.message}\n\nPlease try again or contact support if the issue persists.`);
      } finally {
        setDownloading(false);
      }
    }
  };

  React.useEffect(() => {
    if (!package_) {
      generateApplicationPackage();
    }
  }, []);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Generate Application Package</h3>
        <p className="text-gray-400 text-lg">Your comprehensive visitor visa application package is ready</p>
      </div>

      {generating && (
        <div className="bg-gray-800 p-8 rounded-lg text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <h4 className="text-xl text-white mb-2">Generating Your Application Package</h4>
          <p className="text-gray-400">Creating professional documents and organizing your materials...</p>
        </div>
      )}

      {!generating && package_ && (
        <>
          {/* Success Message */}
          <div className="bg-emerald-900/20 border border-emerald-400 p-8 rounded-lg text-center">
            <div className="text-6xl mb-4">✈️</div>
            <h3 className="text-3xl font-bold text-emerald-300 mb-4">Application Package Ready!</h3>
            <p className="text-emerald-200 text-lg mb-6">
              Your visitor visa application package has been generated and is ready for download.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-emerald-300 font-medium">
              <div>Package ID: {package_.id}</div>
              <div>Application Strength: {package_.score}%</div>
              <div>Visa Type: {formData.visaType?.toUpperCase()}</div>
            </div>
          </div>

          {/* Package Contents */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">📦 Package Contents (All Word Documents)</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {package_.contents.map((item, index) => (
                <div key={index} className="flex items-center bg-gray-700 p-3 rounded">
                  <span className="text-blue-400 mr-3">📝</span>
                  <span className="text-white text-sm">{item}</span>
                  {item.endsWith('.docx') && (
                    <span className="text-blue-300 text-xs ml-auto">.docx</span>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-blue-900/20 border border-blue-400 rounded">
              <p className="text-blue-200 text-sm">
                <strong>📝 All documents are in Microsoft Word format</strong> - Ready for editing and submission
              </p>
            </div>
          </div>

          {/* Download Section */}
          <div className="bg-gray-800 p-6 rounded-lg text-center">
            <h4 className="text-xl font-semibold text-white mb-4">📥 Download Your Application Package</h4>
            <div className="mb-4">
              <p className="text-gray-300 text-sm mb-2">ZIP file containing all Word documents and your uploaded files</p>
              <p className="text-gray-400 text-xs">Professional application package ready for visa submission</p>
            </div>
            <button
              onClick={downloadPackage}
              disabled={downloading}
              className={`px-12 py-6 rounded-xl font-bold text-2xl transition-colors mb-4 flex items-center justify-center mx-auto ${
                downloading 
                  ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              {downloading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-transparent mr-2"></div>
                  Preparing Download...
                </>
              ) : (
                <>
                  <span className="mr-2">📥</span>
                  Download Application Package (.zip)
                </>
              )}
            </button>
            <div className="space-y-2">
              <div className="text-gray-400 text-sm">
                Package ID: <span className="text-blue-400 font-mono">{package_.id}</span>
              </div>
              <div className="text-gray-400 text-sm">
                Package expires: {new Date(package_.expiresAt).toLocaleDateString()}
              </div>
              <div className="text-emerald-400 text-sm">
                ✅ All documents are editable Word files (.docx)
              </div>
            </div>
          </div>
        </>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Interview Preparation
        </button>
        <button
          onClick={onNext}
          disabled={generating}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            !generating
              ? 'bg-blue-500 text-white hover:bg-blue-600'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Complete Application Process →
        </button>
      </div>
    </div>
  );
};

// Visitor & Tourist Visa Step 7: Final Guidance & Next Steps
export const VisitorStep7FinalGuidance = ({ formData, setFormData, onBack, onClose }) => {
  const [acknowledgedSteps, setAcknowledgedSteps] = useState({});

  const nextSteps = [
    {
      id: 'complete-ds160',
      title: 'Complete DS-160 Application',
      description: 'Fill out the official online visa application form',
      icon: '📝',
      critical: true,
      details: [
        'Complete the DS-160 form online at ceac.state.gov',
        'Upload a digital photo that meets requirements',
        'Print the confirmation page with barcode',
        'Keep the confirmation number for your records'
      ]
    },
    {
      id: 'schedule-interview',
      title: 'Schedule Visa Interview',
      description: 'Book your appointment at the US embassy or consulate',
      icon: '📅',
      critical: true,
      details: [
        'Create an account on the embassy\'s appointment system',
        'Pay the visa application fee (typically $160)',
        'Schedule your interview appointment',
        'Print your appointment confirmation'
      ]
    },
    {
      id: 'prepare-documents',
      title: 'Organize Required Documents',
      description: 'Gather and organize all supporting documents',
      icon: '📋',
      critical: true,
      details: [
        'Valid passport with at least 6 months validity',
        'DS-160 confirmation page',
        'Interview appointment confirmation',
        'Recent passport-style photograph',
        'All supporting documents from your package'
      ]
    },
    {
      id: 'attend-interview',
      title: 'Attend Visa Interview',
      description: 'Appear for your scheduled interview appointment',
      icon: '🎤',
      critical: true,
      details: [
        'Arrive early and dress professionally',
        'Bring all required documents in organized folders',
        'Answer questions honestly and confidently',
        'Be prepared to provide additional documents if requested'
      ]
    },
    {
      id: 'track-application',
      title: 'Track Application Status',
      description: 'Monitor your application progress after the interview',
      icon: '📍',
      critical: false,
      details: [
        'Check application status online using your case number',
        'Wait for processing (typically 3-5 business days)',
        'Collect your passport when ready',
        'Review visa details for accuracy'
      ]
    },
    {
      id: 'travel-preparation',
      title: 'Prepare for Travel',
      description: 'Final preparations before your trip to the US',
      icon: '✈️',
      critical: false,
      details: [
        'Book your flights and accommodations',
        'Purchase travel insurance',
        'Prepare customs declaration documents',
        'Keep supporting documents for US entry'
      ]
    }
  ];

  const importantReminders = [
    {
      title: 'Document Authenticity',
      description: 'All documents must be genuine and accurate. Never submit false information.',
      icon: '⚖️'
    },
    {
      title: 'Interview Honesty',
      description: 'Be truthful during your interview. Inconsistencies can lead to visa denial.',
      icon: '🗣️'
    },
    {
      title: 'Purpose Clarity',
      description: 'Clearly demonstrate your legitimate purpose for visiting the United States.',
      icon: '🎯'
    },
    {
      title: 'Return Intent',
      description: 'Show strong ties to your home country and intent to return after your visit.',
      icon: '🏠'
    },
    {
      title: 'Financial Capability',
      description: 'Demonstrate ability to financially support yourself during your stay.',
      icon: '💰'
    },
    {
      title: 'Travel Compliance',
      description: 'Follow all visa conditions and immigration laws during your visit.',
      icon: '📏'
    }
  ];

  const acknowledgeStep = (stepId) => {
    setAcknowledgedSteps({
      ...acknowledgedSteps,
      [stepId]: true
    });
  };

  const getCriticalStepStats = () => {
    const critical = nextSteps.filter(s => s.critical);
    const acknowledged = critical.filter(s => acknowledgedSteps[s.id]);
    return { total: critical.length, acknowledged: acknowledged.length };
  };

  const criticalStats = getCriticalStepStats();
  const allCriticalAcknowledged = criticalStats.acknowledged === criticalStats.total;

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Final Guidance & Next Steps</h3>
        <p className="text-gray-400 text-lg">Complete guide for your visitor visa application process</p>
      </div>

      {/* Critical Steps Progress */}
      <div className="bg-gradient-to-r from-blue-900/20 to-gray-900/20 p-8 rounded-lg border border-blue-400/30">
        <div className="text-center">
          <div className="text-6xl font-bold text-blue-400 mb-2">{criticalStats.acknowledged}/{criticalStats.total}</div>
          <div className="text-xl text-white mb-4">Critical Steps Acknowledged</div>
          <div className={`text-lg ${allCriticalAcknowledged ? 'text-emerald-400' : 'text-yellow-400'}`}>
            {allCriticalAcknowledged ? 'Ready to begin application process!' : 'Please acknowledge all critical steps'}
          </div>
        </div>
      </div>

      {/* Next Steps Checklist */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">📝 Next Steps Checklist</h4>
        <div className="space-y-4">
          {nextSteps.map((step) => (
            <div key={step.id} className="p-4 bg-gray-700 rounded-lg">
              <div className="flex items-start justify-between">
                <div className="flex items-start flex-1">
                  <div 
                    className={`w-6 h-6 rounded border-2 mr-4 flex items-center justify-center mt-1 cursor-pointer ${
                      acknowledgedSteps[step.id] 
                        ? 'bg-emerald-600 border-emerald-600' 
                        : step.critical
                          ? 'border-yellow-500 bg-yellow-500/20'
                          : 'border-gray-500'
                    }`}
                    onClick={() => acknowledgeStep(step.id)}
                  >
                    {acknowledgedSteps[step.id] && (
                      <span className="text-white text-sm">✓</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">{step.icon}</span>
                      <div>
                        <h5 className={`font-medium ${
                          acknowledgedSteps[step.id] ? 'text-white' : 'text-gray-300'
                        }`}>
                          {step.title}
                        </h5>
                        {step.critical && (
                          <span className="ml-2 text-yellow-400 text-xs font-bold">*CRITICAL</span>
                        )}
                      </div>
                    </div>
                    <p className="text-gray-400 text-sm mt-2">{step.description}</p>
                    
                    <div className="mt-3">
                      <h6 className="text-blue-300 text-sm font-medium mb-1">Details:</h6>
                      <ul className="space-y-1">
                        {step.details.map((detail, index) => (
                          <li key={index} className="text-blue-200 text-xs flex items-start">
                            <span className="text-blue-400 mr-1">•</span>
                            {detail}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
                <span className={`text-sm font-medium px-3 py-1 rounded ${
                  acknowledgedSteps[step.id]
                    ? 'bg-emerald-600 text-white' 
                    : step.critical
                      ? 'bg-yellow-500 text-white'
                      : 'bg-gray-600 text-gray-300'
                }`}>
                  {acknowledgedSteps[step.id] ? 'ACKNOWLEDGED' : step.critical ? 'CRITICAL' : 'OPTIONAL'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Important Reminders */}
      <div className="bg-purple-900/20 border border-purple-400 p-6 rounded-lg">
        <h4 className="text-purple-300 font-semibold mb-4">⚡ Important Reminders</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {importantReminders.map((reminder, index) => (
            <div key={index} className="flex items-start p-3 bg-purple-900/30 rounded">
              <span className="text-2xl mr-3">{reminder.icon}</span>
              <div>
                <h5 className="text-purple-200 font-medium text-sm">{reminder.title}</h5>
                <p className="text-purple-300 text-xs mt-1">{reminder.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Application Summary */}
      <div className="bg-emerald-900/20 border border-emerald-400 p-6 rounded-lg">
        <h4 className="text-emerald-300 font-semibold mb-4">📊 Your Application Summary</h4>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-400">{formData.visaType?.toUpperCase()}</div>
            <div className="text-emerald-200 text-sm">Visa Type</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-400">{formData.analysisResults?.overall_score || 0}%</div>
            <div className="text-emerald-200 text-sm">Application Strength</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-400">{(formData.documents || []).length}</div>
            <div className="text-emerald-200 text-sm">Documents Uploaded</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-400">{Math.round((Object.values(formData.preparationProgress || {}).filter(Boolean).length / 4) * 100)}%</div>
            <div className="text-emerald-200 text-sm">Interview Prepared</div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Application Package
        </button>
        <button
          onClick={onClose}
          className="px-6 py-3 rounded-lg font-medium transition-colors bg-emerald-400 text-black hover:bg-emerald-300"
        >
          Complete & Close
        </button>
      </div>
    </div>
  );
};